// frontend/src/components/builder/ComponentPalette.tsx
import React from 'react';
import componentRegistry from '@/registry/componentRegistry';
import { useDraggable } from '@dnd-kit/core';
import { LucideIcon, icons } from 'lucide-react';

// Dynamically get an icon by name
const Icon = ({ name, ...props }: { name: string } & React.ComponentProps<LucideIcon>) => {
  const LucideIcon = icons[name as keyof typeof icons];
  if (!LucideIcon) {
    return null; // or a default icon
  }
  return <LucideIcon {...props} />;
};


function DraggableComponent({ id, data }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id,
    data,
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        touchAction: 'none', // Prevents scrolling when dragging on touch devices
      }}
      {...listeners}
      {...attributes}
      className="p-2 m-2 bg-gray-100 rounded-md shadow-sm flex items-center cursor-grab"
    >
      <Icon name={data.icon} className="h-5 w-5 mr-2 text-gray-600" />
      <div>
        <div className="font-semibold text-sm">{data.name}</div>
        <div className="text-xs text-gray-500">{data.description}</div>
      </div>
    </div>
  );
}


export default function ComponentPalette() {
  const components = Object.values(componentRegistry);
  
  const groupedComponents = components.reduce((acc, entry) => {
    const category = entry.metadata.category || 'General';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(entry);
    return acc;
  }, {} as Record<string, typeof components>);

  return (
    <div className="w-[30%] min-w-[180px] h-full bg-white shadow-md overflow-y-auto overflow-x-hidden border-r flex-shrink-0">
      <div className="p-2 font-bold text-lg border-b sticky top-0 bg-white z-10">Components</div>
      <div className="p-0 flex flex-col gap-4">
        {Object.entries(groupedComponents).map(([category, entries]) => (
          <div key={category} className="mb-2">
            <div className="p-2 pt-2 text-sm font-semibold text-gray-600 border-b mb-2">{category}</div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              {entries.map(({ metadata }) => (
                <DraggableComponent key={metadata.id} id={metadata.id} data={metadata} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
